import React, { useEffect, Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import { makeStyles } from '@material-ui/core/styles';
import Paginator from 'components/Paginator';

import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { Card } from 'components/UI';
import { formatAmount } from 'utils';
import Feature from 'components/Feature';
import { CustomersAction } from 'state/modules/customers';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddCreditsModal } from 'components/Modals';
import Loading from 'components/Loading';

const creditsListSx = {
  maxHeight: 300,
  overflowY: 'auto',
  '& .MuiListItem-root': {
    minHeight: 56,
  },
};

const listItemsSx = {
  maxWidth: '40%',
  minWidth: '50px',
  wordBreak: 'break-all',
  textAlign: 'end',
};

const creditLabelSx = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 'bold',
  color: 'secondary.dark',
  wordBreak: 'break-word',
};

const amountIssuedSx = {
  fontSize: '0.8125rem',
  fontWeight: 'bold',
  color: 'secondary.dark',
  wordBreak: 'break-word',
};

const creditsUsedSx = {
  fontSize: '0.8125rem',
  fontWeight: 'bold',
  color: 'secondary.light',
  wordBreak: 'break-word',
};

// The shared Card (components/UI) is still MUI v4 and forwards `contentRootClasses`
// to CardContent as `classes`, so it needs a class name, not a style object.
const useCardContentStyles = makeStyles({
  root: {
    paddingTop: '24px',
  },
});

const CreditsWidget = ({ user }) => {
  const dispatch = useDispatch();
  const cardContentClasses = useCardContentStyles();
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
  });

  const account = useSelector((state) => state?.customers?.account);
  const credits = useSelector((state) => state?.customers?.credits);
  const creditsPagination = useSelector((state) => state?.customers?.creditsPagination);
  const fetchingCredit = useSelector((state) => state?.customers?.fetchingCredit);
  const fetchingRevokeCredit = useSelector((state) => state?.customers?.fetchingRevokeCredit);

  const loading = isNaN(user?.account_id) || fetchingCredit || fetchingRevokeCredit;

  const [openCreditModal, setOpenCreditModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Handle open edit account modal
   */
  const handleModalOpen = () => {
    setOpenCreditModal((prevState) => !prevState);
  };

  /**
   * Handle revoke credit
   */
  const handleRevokeCredit = (id, account) => {
    dispatch(CustomersAction.fetchRevokeCredit(id, account));
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('payments.add_credit'),
      onClick: () => handleModalOpen(),
      can: { action: 'create', name: 'credit' },
    },
  ];

  useEffect(() => {
    if (user?.account_id) {
      dispatch(CustomersAction.fetchAccountCreditById({ account_id: user.account_id, ...params }));
    }
  }, [user?.account_id, dispatch, params]);

  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
    dispatch(CustomersAction.fetchAccountCreditById({ account_id: user.account_id, ...params }));
  };

  return (
    <Card
      menuItems={items}
      headerActionType="vertical"
      title={i18n.t('common.credits')}
      contentRootClasses={cardContentClasses}
    >
      <Feature name="credit" is="enabled">
        <Can I="read" on="credit">
          <Box>
            {loading ? (
              <Loading />
            ) : credits?.length ? (
              <>
                <List component="nav" aria-labelledby="list" sx={creditsListSx}>
                  {credits?.map((credit, index) => {
                    return (
                      <Fragment key={index}>
                        <ListItem>
                          <ListItemText>
                            <Box display="flex" flexDirection="column">
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 4 }}
                              >
                                <Box fontSize={13} component="span">
                                  {credit.display_name}
                                </Box>
                              </Typography>
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 16 }}
                              >
                                <Box fontSize={13} component="span">
                                  <Box component="span" sx={creditLabelSx}>
                                    {i18n.t('common.expiration_date')}:
                                  </Box>
                                  {` ${moment(credit.expiration).format(
                                    `${localeFormat} (UTC Z)`
                                  )}`}
                                </Box>
                              </Typography>
                              <Typography
                                color="textSecondary"
                                style={{ lineHeight: 1.2, marginRight: 89, marginBottom: 4 }}
                              >
                                <Box fontSize={13} component="span">
                                  <Box component="span" sx={creditLabelSx}>
                                    {i18n.t('common.description')}:
                                  </Box>
                                  {credit.description}
                                </Box>
                              </Typography>
                            </Box>
                          </ListItemText>
                          <ListItemSecondaryAction sx={listItemsSx}>
                            <Box display="flex" alignItems="center">
                              <Box display="flex" flexDirection="column">
                                <Typography color="textSecondary" style={{ lineHeight: 1.2 }}>
                                  <Box fontSize={12} component="span" sx={amountIssuedSx}>
                                    $ {formatAmount(credit.amount_issued)}
                                  </Box>
                                </Typography>
                                <Typography style={{ lineHeight: 1.2 }}>
                                  <Box fontSize={10} component="span" sx={creditsUsedSx}>
                                    $ {formatAmount(credit.amount_consumed)}
                                  </Box>
                                </Typography>
                              </Box>
                              {credit.is_valid && (
                                <IconButton
                                  component="span"
                                  aria-label="Remove credit"
                                  disabled={fetchingRevokeCredit}
                                  onClick={() => handleRevokeCredit(credit.id, user.account_id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                        <Divider />
                      </Fragment>
                    );
                  })}
                </List>
                <Paginator
                  component="div"
                  limit={params.limit}
                  pagination={creditsPagination}
                  onPageChange={handleChangePage}
                />
              </>
            ) : (
              <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                <Box fontSize={14} component="span" lineHeight={1}>
                  {i18n.t('customers.no_credits')}
                </Box>
              </Typography>
            )}
          </Box>
        </Can>
      </Feature>

      {openCreditModal && (
        <AddCreditsModal
          to="driver"
          type="credit"
          user={user}
          account={account}
          accountLevel={true}
          open={openCreditModal}
          handleClick={() => handleModalOpen()}
        />
      )}
    </Card>
  );
};

CreditsWidget.propTypes = {
  user: PropTypes.shape({
    account_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export { CreditsWidget };
